import type { FC, ReactNode } from 'react'
import type { Job } from '@/types'

import { cn } from '@/lib/utils'
import { useNow } from '@/hooks/use-now'
import { MINUTE, formatClock, formatMinutes } from '@/lib/carwash/format'
import { getElapsedMs, getOverrunMinutes, getWaitMs } from '@/lib/carwash/selectors'

const isRunning = (job: Job): boolean => job.status === 'washing' || job.status === 'quality_check'

const isQueued = (job: Job): boolean => job.status === 'waiting' || job.status === 'assigned'

/* Re-renders every second only while the job is live. */
export const useJobNow = (job: Job): number => useNow(isRunning(job) || isQueued(job) ? 1000 : 3_600_000)

/* The big hh:mm:ss timer. Washing time when started, otherwise time in the queue. */
export const JobClock: FC<{ job: Job, className?: string }> = ({ job, className }): ReactNode => {
    const now = useJobNow(job)

    if (!now) return <span className={className}>--:--:--</span>

    const value = job.startedAt ? getElapsedMs(job, now) : getWaitMs(job, now)

    return (
        <span className={cn('tabular-nums', className)} suppressHydrationWarning>
            {formatClock(value)}
        </span>
    )
}

/* "18 min" style elapsed or waiting time. */
export const JobMinutes: FC<{ job: Job, className?: string }> = ({ job, className }): ReactNode => {
    const now = useJobNow(job)

    if (!now) return <span className={className}>&nbsp;</span>

    const value = job.startedAt ? getElapsedMs(job, now) : getWaitMs(job, now)

    return (
        <span className={cn('tabular-nums', className)} suppressHydrationWarning>
            {formatMinutes(value / MINUTE)}
        </span>
    )
}

/* Elapsed against the estimate, with a plain-language label underneath. */
export const JobProgress: FC<{ job: Job, className?: string, compact?: boolean }> = ({ job, className, compact = false }): ReactNode => {
    const now = useJobNow(job)

    if (!job.startedAt || !now) return null

    const elapsed = getElapsedMs(job, now)
    const ratio = Math.min(1, elapsed / Math.max(1, job.estimatedMinutes * MINUTE))
    const overrun = getOverrunMinutes(job, now)
    const remaining = Math.max(0, Math.ceil(job.estimatedMinutes - elapsed / MINUTE))

    const tone = overrun > 0 ? 'bg-red-500' : ratio > 0.8 ? 'bg-amber-500' : 'bg-primary'
    const label = overrun > 0
        ? `${formatMinutes(Math.ceil(overrun))} over estimate`
        : remaining <= 0 ? 'Due now' : `About ${formatMinutes(remaining)} left`

    return (
        <div className={className}>
            <div
                className='h-1.5 overflow-hidden rounded-full bg-slate-200'
                role='progressbar'
                aria-label='Wash progress'
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(ratio * 100)}
            >
                <div className={cn('h-full rounded-full transition-[width] duration-700', tone)} style={{ width: `${Math.max(4, ratio * 100)}%` }} />
            </div>

            {!compact && (
                <p className={cn('mt-1.5 text-xs font-medium', overrun > 0 ? 'text-red-700' : 'text-muted-foreground')} suppressHydrationWarning>
                    {label}
                    <span className='text-slate-400'>
                        {` · est. ${formatMinutes(job.estimatedMinutes)}`}
                    </span>
                </p>
            )}
        </div>
    )
}
